import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdRecommendedCard.css';

const AdRecommendedCard = ({ adData, recommendedNews = [], isActive }) => {
    const navigate = useNavigate();
    // Initialize mute state to false so ads play with sound by default
    const [isMuted, setIsMuted] = useState(false);
    const videoRef = useRef(null);
    const [videoPlayTriggered, setVideoPlayTriggered] = useState(false);

    // --- RESOLVE MEDIA FIELD NAMES ---
    const videoSrc = adData?.videoUrl || adData?.uploadedImageUrl;
    const isVideo = adData?.mediaType === 'video' || (videoSrc && (videoSrc.includes('.mp4') || videoSrc.includes('video')));
    
    const imageSrc = adData?.imageUrls?.[0] || adData?.uploadedImageUrls?.[0] || adData?.imageUrl;

    // Synchronize native player volume/unmute interactions back with React state
    const handleVolumeChange = () => {
        if (videoRef.current) {
            setIsMuted(videoRef.current.muted);
        }
    };

    useEffect(() => {
        if (!isVideo || !videoRef.current || !videoSrc) return;

        if (isActive) {
            const timer = setTimeout(() => {
                if (videoRef.current) {
                    // Attempt to play with the user's preferred audio state (defaults to unmuted)
                    videoRef.current.muted = isMuted;
                    videoRef.current.play()
                        .then(() => setVideoPlayTriggered(true))
                        .catch(err => {
                            console.log("Autoplay with sound was prevented, falling back to muted play:", err);
                            // Fallback to muted autoplay only if browser policy restricts audio startup
                            if (videoRef.current) {
                                videoRef.current.muted = true;
                                setIsMuted(true);
                                videoRef.current.play()
                                    .then(() => setVideoPlayTriggered(true))
                                    .catch(() => {});
                            }
                        });
                }
            }, 50); // 50ms layout delay
            return () => clearTimeout(timer);
        } else {
            if (videoRef.current) {
                videoRef.current.pause();
            }
            setVideoPlayTriggered(false);
        }
    }, [isActive, isVideo, videoSrc]);

    // Click retry listener
    useEffect(() => {
        if (!isActive || !isVideo || videoPlayTriggered || !videoRef.current) return;

        const resumePlayback = () => {
            if (videoRef.current) {
                videoRef.current.muted = isMuted;
                videoRef.current.play()
                    .then(() => {
                        setVideoPlayTriggered(true);
                        window.removeEventListener('click', resumePlayback);
                    })
                    .catch(() => {});
            }
        };

        window.addEventListener('click', resumePlayback);
        return () => window.removeEventListener('click', resumePlayback);
    }, [isActive, isVideo, videoPlayTriggered, isMuted]);

    const handleNewsNavigation = (e, id) => {
        e.stopPropagation();
        navigate(`/news/${id}`);
    };

    return (
        <div className="news-card ad-rec-card-container">
            {/* Top Ad Media Panel */}
            <div className="ad-rec-banner-frame">
                <span className="ad-rec-tag-indicator">Advertise on AiNews</span>
                {isVideo ? (
                    <video
                        ref={videoRef}
                        key={videoSrc}
                        src={videoSrc}
                        className="ad-rec-media"
                        controls // Enables native play/pause, timeline seek, and volume control
                        muted={isMuted} // Linked directly to React's mute tracker
                        onVolumeChange={handleVolumeChange} // Syncs native volume modifications back to state
                        loop
                        playsInline
                        onClick={(e) => e.stopPropagation()}
                        style={{ width: '100%', height: '100%', objectFit: 'contain', backgroundColor: '#000' }}
                    />
                ) : imageSrc ? (
                    <img 
                        src={imageSrc} 
                        alt="Ad" 
                        className="ad-rec-media" 
                        style={{ width: '100%', height: '100%', objectFit: 'contain', backgroundColor: '#000' }}
                    />
                ) : (
                    <div className="ad-rec-gradient-fallback">Sponsored Content</div>
                )}
            </div>

            {/* Structured Recommended Cards Section */}
            <div className="ad-rec-details-panel" onClick={(e) => e.stopPropagation()}>
                <h3 className="ad-rec-panel-header">Recommended News</h3>
                
                <div className="ad-rec-items-list-wrapper">
                    {recommendedNews.slice(0, 3).map((item) => (
                        <div 
                            key={item.id} 
                            className="ad-rec-white-card"
                            onClick={(e) => handleNewsNavigation(e, item.id)}
                        >
                            <div className="ad-rec-card-body">
                                {item.imageUrls?.[0] && (
                                    <img src={item.imageUrls[0]} alt="" className="ad-rec-item-thumbnail" />
                                )}
                                <div className="ad-rec-item-text-column">
                                    <span className="ad-rec-item-category-tag">
                                        {item.subCategory?.[0] || 'Top News'}
                                    </span>
                                    <h4 className="ad-rec-item-headline">{item.title}</h4>
                                </div>
                            </div>
                            
                            <div className="ad-rec-card-footer-action">
                                View Post (పూర్తి వార్త చూడండి)
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default AdRecommendedCard;